import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { WORKER_URL } from '../../utils/api';

export default function Login({ initialMode = 'login' }) {
  const { login, register } = useAuth();
  // mode: 'login' | 'register' | 'forgot'
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(1); // 1=email, 2=código+dados+telefone, 3=código SMS
  const [form, setForm] = useState({ email: '', password: '', name: '', code: '', newPassword: '', phone: '', smsCode: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [referralCode] = useState(() => localStorage.getItem('referral_code') || '');
  const [referrerName, setReferrerName] = useState('');

  useEffect(() => {
    if (!referralCode || mode !== 'register') return;
    fetch(`${WORKER_URL}/api/referral/lookup?code=${encodeURIComponent(referralCode)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.name) setReferrerName(d.name); })
      .catch(() => {});
  }, [referralCode, mode]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const switchMode = (newMode) => {
    setMode(newMode);
    setStep(1);
    setError('');
    setSuccess('');
    setForm({ email: '', password: '', name: '', code: '', newPassword: '', phone: '', smsCode: '' });
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'register' ? '/api/auth/send-code' : '/api/auth/forgot-password';
      const res = await fetch(`${WORKER_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao enviar código');
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendSmsCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Nome é obrigatório'); return; }
    if (form.password.length < 6) { setError('Senha deve ter no mínimo 6 caracteres'); return; }
    if (!form.phone.trim()) { setError('Celular é obrigatório'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${WORKER_URL}/api/auth/send-sms-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, emailCode: form.code, phone: form.phone })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao enviar SMS');
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(form.email, form.password, form.name, form.code, form.phone, form.smsCode, referralCode || undefined);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (form.newPassword.length < 6) { setError('Senha deve ter no mínimo 6 caracteres'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${WORKER_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, code: form.code, password: form.newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao redefinir senha');
      setSuccess('Senha redefinida com sucesso! Faça login.');
      setTimeout(() => switchMode('login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const subtitle = {
    login: 'Entre na sua conta',
    register: step === 1 ? 'Comece seus 7 dias grátis' : step === 2 ? 'Verifique seu email' : 'Verifique seu celular',
    forgot: step === 1 ? 'Recuperar senha' : 'Verifique seu email',
  }[mode];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f2f5' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '40px', width: '100%', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src="/logo-integra-mkp.png" alt="Integra MKP" style={{ width: '200px', height: 'auto', marginBottom: '8px' }} />
          <p style={{ color: '#999', margin: '4px 0 0', fontSize: '0.85rem' }}>{subtitle}</p>
        </div>

        {success && (
          <div style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', color: '#276749', padding: '10px 14px', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '16px', textAlign: 'center' }}>
            {success}
          </div>
        )}

        {/* LOGIN */}
        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Email</label>
              <input type="email" value={form.email} onChange={set('email')} placeholder="seu@email.com" required style={inputStyle} />
            </div>
            <div style={{ marginBottom: '8px' }}>
              <label style={labelStyle}>Senha</label>
              <input type="password" value={form.password} onChange={set('password')} placeholder="••••••••" required style={inputStyle} />
            </div>
            <div style={{ textAlign: 'right', marginBottom: '20px' }}>
              <button type="button" onClick={() => switchMode('forgot')} style={{ background: 'none', border: 'none', color: '#2D3277', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}>
                Esqueceu a senha?
              </button>
            </div>
            {error && <ErrorBox msg={error} />}
            <button type="submit" disabled={loading} style={btnStyle(loading)}>
              {loading ? 'Aguarde...' : 'Entrar'}
            </button>
          </form>
        )}

        {/* REGISTER step 1 */}
        {mode === 'register' && step === 1 && (
          <form onSubmit={handleSendCode}>
            {referrerName && (
              <div style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', color: '#276749', padding: '10px 14px', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '20px', textAlign: 'center' }}>
                🎁 Indicado por <strong>{referrerName}</strong> — você ganha <strong>15% off</strong> na primeira mensalidade!
              </div>
            )}
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Email</label>
              <input type="email" value={form.email} onChange={set('email')} placeholder="seu@email.com" required style={inputStyle} />
            </div>
            {error && <ErrorBox msg={error} />}
            <button type="submit" disabled={loading} style={btnStyle(loading)}>
              {loading ? 'Enviando...' : 'Enviar código de verificação'}
            </button>
          </form>
        )}

        {/* REGISTER step 2 */}
        {mode === 'register' && step === 2 && (
          <form onSubmit={handleSendSmsCode}>
            <InfoBox msg={`Código enviado para ${form.email}. Verifique sua caixa de entrada.`} />
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Código de verificação (email)</label>
              <input type="text" value={form.code} onChange={set('code')} placeholder="000000" maxLength={6} required autoFocus
                style={{ ...inputStyle, letterSpacing: '6px', fontSize: '1.2rem', textAlign: 'center' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Nome</label>
              <input type="text" value={form.name} onChange={set('name')} placeholder="Seu nome" required style={inputStyle} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Senha</label>
              <input type="password" value={form.password} onChange={set('password')} placeholder="Mínimo 6 caracteres" minLength={6} required style={inputStyle} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Celular (WhatsApp)</label>
              <input type="tel" value={form.phone} onChange={set('phone')} placeholder="(11) 99999-9999" required style={inputStyle} />
            </div>
            {error && <ErrorBox msg={error} />}
            <button type="submit" disabled={loading} style={btnStyle(loading)}>
              {loading ? 'Enviando SMS...' : 'Enviar código por SMS'}
            </button>
            <ResendLink onClick={() => { setStep(1); setError(''); setForm(f => ({ ...f, code: '' })); }} />
          </form>
        )}

        {/* REGISTER step 3 */}
        {mode === 'register' && step === 3 && (
          <form onSubmit={handleRegister}>
            <InfoBox msg={`Código SMS enviado para ${form.phone}. Verifique suas mensagens.`} />
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Código de verificação (SMS)</label>
              <input type="text" value={form.smsCode} onChange={set('smsCode')} placeholder="000000" maxLength={6} required autoFocus
                style={{ ...inputStyle, letterSpacing: '6px', fontSize: '1.2rem', textAlign: 'center' }} />
            </div>
            {error && <ErrorBox msg={error} />}
            <button type="submit" disabled={loading} style={btnStyle(loading)}>
              {loading ? 'Criando conta...' : 'Criar conta'}
            </button>
            <ResendLink onClick={() => { setStep(2); setError(''); setForm(f => ({ ...f, smsCode: '' })); }} />
          </form>
        )}

        {/* FORGOT step 1 */}
        {mode === 'forgot' && step === 1 && (
          <form onSubmit={handleSendCode}>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Email da sua conta</label>
              <input type="email" value={form.email} onChange={set('email')} placeholder="seu@email.com" required style={inputStyle} />
            </div>
            {error && <ErrorBox msg={error} />}
            <button type="submit" disabled={loading} style={btnStyle(loading)}>
              {loading ? 'Enviando...' : 'Enviar código de recuperação'}
            </button>
          </form>
        )}

        {/* FORGOT step 2 */}
        {mode === 'forgot' && step === 2 && (
          <form onSubmit={handleResetPassword}>
            <InfoBox msg={`Código enviado para ${form.email}. Verifique sua caixa de entrada.`} />
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Código de verificação</label>
              <input type="text" value={form.code} onChange={set('code')} placeholder="000000" maxLength={6} required autoFocus
                style={{ ...inputStyle, letterSpacing: '6px', fontSize: '1.2rem', textAlign: 'center' }} />
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Nova senha</label>
              <input type="password" value={form.newPassword} onChange={set('newPassword')} placeholder="Mínimo 6 caracteres" minLength={6} required style={inputStyle} />
            </div>
            {error && <ErrorBox msg={error} />}
            <button type="submit" disabled={loading} style={btnStyle(loading)}>
              {loading ? 'Redefinindo...' : 'Redefinir senha'}
            </button>
            <ResendLink onClick={() => { setStep(1); setError(''); setForm(f => ({ ...f, code: '', newPassword: '' })); }} />
          </form>
        )}

        {/* Toggle login/register */}
        {mode !== 'forgot' && (
          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.85rem', color: '#666' }}>
            {mode === 'login' ? 'Não tem conta? ' : 'Já tem conta? '}
            <button onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
              style={{ background: 'none', border: 'none', color: '#2D3277', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}>
              {mode === 'login' ? 'Cadastre-se' : 'Entrar'}
            </button>
          </p>
        )}

        {mode === 'forgot' && (
          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.85rem', color: '#666' }}>
            <button onClick={() => switchMode('login')}
              style={{ background: 'none', border: 'none', color: '#2D3277', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}>
              Voltar ao login
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{ backgroundColor: '#fff0f0', border: '1px solid #ffcccc', color: '#cc0000', padding: '10px 14px', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '16px' }}>
      {msg}
    </div>
  );
}

function InfoBox({ msg }) {
  return (
    <div style={{ backgroundColor: '#f0f7ff', border: '1px solid #c8e0ff', color: '#1a56db', padding: '10px 14px', borderRadius: '6px', fontSize: '0.85rem', marginBottom: '20px' }}>
      {msg}
    </div>
  );
}

function ResendLink({ onClick }) {
  return (
    <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '0.8rem', color: '#999' }}>
      Não recebeu?{' '}
      <button type="button" onClick={onClick}
        style={{ background: 'none', border: 'none', color: '#2D3277', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>
        Reenviar código
      </button>
    </p>
  );
}

const btnStyle = (loading) => ({
  width: '100%', padding: '12px', backgroundColor: '#2D3277', color: 'white', border: 'none',
  borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
  opacity: loading ? 0.7 : 1, transition: 'opacity 0.2s',
});

const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 500, color: '#444' };

const inputStyle = {
  width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px',
  fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
};
